import {
  OutliersLimits,
  Report,
  ReportById,
  ReportStatus,
  ReportTableData,
  SearchResponse,
} from "interfaces/dataInterfaces"
import { blobToDownload, normalizeText } from "utils/helpers"
import { marketpricesApi } from "./marketpricesApi"
import { ReportStatuses } from "../../constants/prices"

export const reportsApi = marketpricesApi.injectEndpoints({
  endpoints: builder => ({
    deleteReport: builder.mutation<void, number>({
      query: id => ({ url: `Report/Delete/${id}`, method: "DELETE" }),
      invalidatesTags: [
        { type: "Report", id: "LIST" },
        { type: "Report", id: "SEARCH_LIST" },
        { type: "GorpReports" as const, id: "LIST" },
      ],
    }),

    getReport: builder.query<
      SearchResponse<Report>,
      { skip: number; take: number; adm0Code: number; status: number; reportTypeId?: number }
    >({
      query: ({ skip, take, adm0Code, status, reportTypeId = 1 }) => ({
        url: "Report/Get",
        method: "GET",
        params: { skip, take, adm0Code, status, reportTypeId },
      }),
      providesTags: [{ type: "Report" as const, id: "LIST" }],
    }),

    changeReportStatus: builder.mutation<void, { id: number; status: number; previousApprovedStatus?: number }>({
      query: ({ id, status, previousApprovedStatus }) => ({
        url: "Report/ChangeReportStatus/changereportstatus",
        method: "PUT",
        params: { reportid: id, status, previousApprovedStatus },
      }),
      invalidatesTags: [
        { type: "Report", id: "LIST" },
        { type: "Report", id: "SEARCH_LIST" },
      ],
    }),

    getReportData: builder.query<ReportTableData[], number>({
      query: id => ({ url: "Report/GetReportData/GetReportData", method: "GET", params: { id } }),
    }),

    getReportById: builder.query<ReportById, number>({
      query: id => ({ url: `Report/Get/${id}`, method: "GET" }),
    }),

    saveReport: builder.mutation<
      { reportID: number; result: string },
      { adm0Code: number; reportComment: string; mappingSchemaId: number; reportTypeId?: number }
    >({
      query: body => ({
        url: "Report/Post",
        method: "POST",
        body: { ...body, reportStatusId: 1, reportTypeId: body.reportTypeId || 1 },
      }),
      invalidatesTags: [
        { type: "Report", id: "LIST" },
        { type: "Report", id: "SEARCH_LIST" },
      ],
    }),

    postReport: builder.mutation<
      { errorList: string[]; warningList: string[]; extendedItems: ReportTableData[] } | undefined,
      { cid: number; msid: number; rid: number; file: FormData }
    >({
      query: ({ file, ...query }) => ({
        url: "Report/PostReport/postreport",
        method: "POST",
        params: query,
        body: file,
      }),
      invalidatesTags: ["ReportObs"],
    }),

    saveDataReport: builder.mutation<void, { prices: ReportTableData[]; outliers: OutliersLimits[]; reportId: string }>(
      {
        query: body => ({
          url: "Report/SaveDataReport/savedatareport",
          method: "POST",
          params: { rid: body.reportId },
          body,
        }),
        invalidatesTags: [{ type: "Report", id: "LIST" }],
      },
    ),

    downloadReportExcelFile: builder.mutation<
      void,
      { reportId: string; countryName?: string; startDate?: string; endDate?: string }
    >({
      query: ({ reportId, countryName, startDate, endDate }) => ({
        url: "Report/DownloadOriginReport/downloadoriginreport",
        method: "GET",
        params: { reportId },
        responseHandler: async response => {
          const country = normalizeText(countryName) || ""
          const reportStartDate = normalizeText(startDate?.split("T")[0]) || ""
          const reportEndDate = normalizeText(endDate?.split("T")[0]) || ""
          const parts = ["Market_Price", country, reportStartDate, reportEndDate].filter(Boolean)
          const fileName = `${parts.join("_")}.xlsx`
          await blobToDownload(response, fileName, "Report file not found")
        },
      }),
    }),

    restoreReportObs: builder.mutation<{ observationsCreated: number }, number>({
      query: reportId => ({
        url: "Report/DisaggregatePricesToObservations/disaggregatepricesToobservations",
        method: "POST",
        params: { reportId },
      }),
      invalidatesTags: ["ReportObs"],
    }),

    getReportStatus: builder.query<Array<ReportStatus>, void>({
      query: () => ({ url: "Report/GetStatuses/getstatuses", method: "GET" }),
      providesTags: ["ReportStatus"],
      transformResponse: (response: ReportStatus[]) =>
        response?.filter(item => Object.values(ReportStatuses)?.includes(item.id)) ?? [],
    }),
  }),
})

export const {
  useDeleteReportMutation,
  useGetReportQuery,
  useChangeReportStatusMutation,
  useGetReportDataQuery,
  useGetReportByIdQuery,
  useSaveReportMutation,
  useSaveDataReportMutation,
  usePostReportMutation,
  useDownloadReportExcelFileMutation,
  useRestoreReportObsMutation,
  useGetReportStatusQuery,
} = reportsApi
